"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { useAuth } from "@/lib/auth";
import apiClient from "@/lib/api";
import PageTitleHeader from "@/components/shared/PageTitleHeader";
import GuideNotice from "@/components/shared/GuideNotice";
import InterviewContextPicker from "./InterviewContextPicker";
import InterviewerSelector from "./InterviewerSelector";
import {
  DEFAULT_INTERVIEWER_KEY,
  interviewerIdleClipSrc,
  isInterviewerSelectionEnabled,
  resolveInterviewer,
  type InterviewerKey,
} from "@/lib/interviewers";
import { listTargetPositions, type TargetPosition } from "@/lib/api/targetPositions";
import { listJobHuntingFiles, type JobHuntingFile } from "@/lib/api/jobHuntingFiles";
import {
  INTERVIEW_QUESTION_COUNT_OPTIONS,
  RECOMMENDED_INTERVIEW_QUESTION_COUNT,
  type InterviewQuestionCount,
} from "@/data/interviewQuestionCount";

type QuestionCount = InterviewQuestionCount;
type SelectionMode = "auto" | "manual";
type JobTypeFilter = "engineer" | "non_engineer";

const JOB_TYPE_OPTIONS: Array<{ value: JobTypeFilter; label: string }> = [
  { value: "non_engineer", label: "総合職" },
  { value: "engineer", label: "エンジニア" },
];

// 職種フィルタの表示フラグ。現状は新卒の出題母集団に職種タグ付き質問が無く
// フィルタが機能しない（エンジニア選択は0件・総合職選択は無効果）ため、
// 総合職方面の質問拡充まで全ユーザーで非表示にする。定義は残し、true に戻すだけで復活できる。
const SHOW_JOB_TYPE_FILTER = false;

// おすすめバッジ（このブランチのスタイル: bg-primary）
function RecommendedBadge() {
  return (
    <span className="absolute -top-2 right-2 bg-primary text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
      おすすめ
    </span>
  );
}

export default function InterviewSetupClient() {
  const { user, loading: authLoading } = useAuth();
  const router = useRouter();
  const searchParams = useSearchParams();
  const [questionCount, setQuestionCount] = useState<QuestionCount>(1);
  const [selectionMode, setSelectionMode] = useState<SelectionMode>("auto");
  // 面接官の選択（#1863）。ページ内の状態として持ち、セッション作成時に送る。ブラウザには記憶させない。
  // 選択の UI は素材と声が揃った環境でだけ出す。出さない環境では既定の女性面接官のまま。
  const interviewerSelectionEnabled = isInterviewerSelectionEnabled();
  const [interviewerKey, setInterviewerKey] = useState<InterviewerKey>(DEFAULT_INTERVIEWER_KEY);
  const interviewer = resolveInterviewer(interviewerSelectionEnabled ? interviewerKey : DEFAULT_INTERVIEWER_KEY);
  const [jobTypeFilters, setJobTypeFilters] = useState<JobTypeFilter[]>([]);
  const [targetPositions, setTargetPositions] = useState<TargetPosition[]>([]);
  const [jobHuntingFiles, setJobHuntingFiles] = useState<JobHuntingFile[]>([]);
  const [targetPositionId, setTargetPositionId] = useState<string | null>(null);
  const [jobHuntingFileIds, setJobHuntingFileIds] = useState<string[]>([]);
  const [contextLoadError, setContextLoadError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // トーストの内容と発火 ID をセットで持ち、同じメッセージが連打されてもタイマーが
  // リセットされるようにする（id を更新するため useEffect 依存が必ず変化する）
  const [toast, setToast] = useState<{ message: string; id: number } | null>(null);
  const [showQuitModal, setShowQuitModal] = useState(false);

  const modalNoButtonRef = useRef<HTMLButtonElement>(null);
  const modalYesButtonRef = useRef<HTMLButtonElement>(null);

  const readyJobHuntingFiles = useMemo(
    () => jobHuntingFiles.filter((f) => f.extracted_text_ready),
    [jobHuntingFiles]
  );

  const toggleJobType = (value: JobTypeFilter) => {
    setJobTypeFilters((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  const switchToManual = () => {
    setSelectionMode("manual");
    setJobTypeFilters([]);
  };

  const showToast = (message: string) => {
    setToast({ message, id: Date.now() });
  };

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(t);
  }, [toast]);

  // モーダル A11y: Escape で閉じる + オープン時にいいえボタンへフォーカス
  useEffect(() => {
    if (!showQuitModal) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setShowQuitModal(false);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    modalNoButtonRef.current?.focus();
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [showQuitModal]);

  // モーダル内 Tab フォーカストラップ（いいえ <-> はい の 2 要素間で循環）
  const handleModalKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== "Tab") return;
    const buttons = [modalNoButtonRef.current, modalYesButtonRef.current].filter(
      Boolean
    ) as HTMLButtonElement[];
    if (buttons.length < 2) return;

    const active = document.activeElement;
    const first = buttons[0];
    const last = buttons[buttons.length - 1];

    if (e.shiftKey && active === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  };

  useEffect(() => {
    if (authLoading) return;
    if (!user) router.push("/");
  }, [user, authLoading, router]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const [tpRes, jhfRes] = await Promise.all([
          listTargetPositions({ per_page: 50 }),
          listJobHuntingFiles({ per_page: 50 }),
        ]);
        if (cancelled) return;
        setTargetPositions(tpRes.items);
        setJobHuntingFiles(jhfRes.items);

        const initialTp = searchParams.get("target_position_id");
        const initialJhfs = searchParams.getAll("job_hunting_file_ids");
        if (initialTp && tpRes.items.some((tp) => tp.id === initialTp)) {
          setTargetPositionId(initialTp);
        }
        if (initialJhfs.length > 0) {
          const readyIds = new Set(
            jhfRes.items.filter((f) => f.extracted_text_ready).map((f) => f.id)
          );
          setJobHuntingFileIds(initialJhfs.filter((id) => readyIds.has(id)));
        }
      } catch {
        if (!cancelled) setContextLoadError(true);
      }
    })();
    return () => {
      cancelled = true;
    };
    // 初回マウント時のみ実施。searchParams が変わってもフェッチし直さない。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (authLoading || !user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-full max-w-lg px-4">
          <div className="bg-surface rounded-2xl p-8 h-96 animate-pulse" />
        </div>
      </div>
    );
  }

  const buildContextQuery = () => {
    const qs = new URLSearchParams();
    qs.set("count", String(questionCount));
    // 自分で選ぶ経路でも面接官の選択を落とさない（#1863）。既定のときは付けず URL を変えない
    if (interviewer.key !== DEFAULT_INTERVIEWER_KEY) qs.set("interviewer_key", interviewer.key);
    if (targetPositionId) qs.set("target_position_id", targetPositionId);
    for (const id of jobHuntingFileIds) qs.append("job_hunting_file_ids", id);
    return qs.toString();
  };

  const handleStart = async () => {
    if (selectionMode === "manual") {
      router.push(`/interview/select?${buildContextQuery()}`);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const payload: Record<string, unknown> = {
        question_count: questionCount,
        selection_mode: "auto",
        interviewer_key: interviewer.key,
      };
      // 多重選択 UI: 1 つだけ選ばれている時のみ絞り込む。0 個または両方 (= 全選択)
      // のときはフィルタなしで送る (バックエンドは単一値か nil を期待する)。
      if (jobTypeFilters.length === 1) payload.selected_job_type = jobTypeFilters[0];
      if (targetPositionId) payload.target_position_id = targetPositionId;
      if (jobHuntingFileIds.length > 0) payload.job_hunting_file_ids = jobHuntingFileIds;

      const res = await apiClient.post("/interview_sessions", payload);
      router.push(`/interview/session/${res.data.id}`);
    } catch (e: unknown) {
      const message =
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ??
        "セッションの作成に失敗しました。もう一度お試しください。";
      setError(message);
      setIsLoading(false);
    }
  };

  const ctaLabel = isLoading
    ? "準備中..."
    : selectionMode === "manual"
      ? "質問を選ぶ"
      : "面接を始める";

  return (
    <div className="min-h-screen bg-background">
      {toast && (
        <div
          role="status"
          aria-live="polite"
          className="fixed top-6 left-1/2 -translate-x-1/2 z-50 bg-on-surface text-surface text-sm font-semibold px-5 py-2.5 rounded-full shadow-lg"
        >
          {toast.message}
        </div>
      )}

      <PageTitleHeader
        imageSrc="/images/mentai-senpai_03_standing06.png"
        title="面接モード"
        subtitle="本番さながらの模擬面接を体験しましょう"
        containerClassName="max-w-6xl mx-auto px-4 md:px-10 py-6 md:py-8"
      />

      <div className="max-w-6xl mx-auto px-4 md:px-10 mt-4">
        <GuideNotice />
      </div>

      <div className="max-w-6xl mx-auto px-4 md:px-10 mt-6 md:mt-8 grid grid-cols-1 md:grid-cols-3 md:gap-6 md:items-start">
        {/* 左カード: 設定フォーム */}
        <div className="md:col-span-2 bg-surface custom-shadow rounded-lg p-5">
          {/* 面接官プレビュー（選択中の面接官の idle 動画）。
              key で選択ごとに要素を作り直し、前の面接官のフレームが残らないようにする（#1863） */}
          <div
            className="relative w-full aspect-video rounded-lg overflow-hidden bg-gray-100 mb-5"
            onContextMenu={(e) => e.preventDefault()}
            onDragStart={(e) => e.preventDefault()}
            draggable={false}
          >
            <video
              key={interviewer.key}
              className="absolute inset-0 w-full h-full object-cover"
              src={interviewerIdleClipSrc(interviewer)}
              autoPlay
              loop
              muted
              playsInline
              preload="auto"
              controlsList="nodownload"
              aria-label="AI面接官のプレビュー"
            />
          </div>

          <p className="text-sm text-on-surface mb-2 leading-relaxed">
            {interviewerSelectionEnabled
              ? "面接官AIが選択した内容に基づいて質問します。面接官と質問数、出題スタイルを選んでください。"
              : "面接官AIが選択した内容に基づいて質問します。質問数と出題スタイルを選んでください。"}
          </p>
          <p className="text-sm text-on-surface mb-6 leading-relaxed">
            コンテキストから履歴書や職務経歴書、面接予定企業を選択すると、AIが内容に応じた質問やフィードバックを生成します。
          </p>

          {/* 面接官（#1863）。素材と声が揃った環境でだけ出す */}
          {interviewerSelectionEnabled && (
            <div className="mb-6">
              <p className="text-sm font-bold mb-3">面接官</p>
              <InterviewerSelector value={interviewerKey} onChange={setInterviewerKey} disabled={isLoading} />
            </div>
          )}

          {/* 質問数 */}
          <div className="mb-6">
            <p className="text-sm font-bold mb-3">質問数</p>
            <div className="flex flex-col sm:flex-row gap-3">
              {INTERVIEW_QUESTION_COUNT_OPTIONS.map((count) => {
                const isSelected = questionCount === count;
                const isRecommended =
                  count === RECOMMENDED_INTERVIEW_QUESTION_COUNT;
                return (
                  <button
                    key={count}
                    type="button"
                    onClick={() => setQuestionCount(count)}
                    className={`relative flex-1 py-4 px-3 rounded-xl border transition-all text-center ${
                      isSelected
                        ? "border-primary border-2 bg-white"
                        : "border-gray-300 bg-white hover:border-primary/50"
                    }`}
                  >
                    {isRecommended && <RecommendedBadge />}
                    <span
                      className={`block text-base font-bold ${
                        isSelected ? "text-primary" : "text-on-surface"
                      }`}
                    >
                      最大{count * 2}問
                    </span>
                    <span
                      className={`block text-[11px] font-medium mt-1 leading-relaxed ${
                        isSelected ? "text-primary/80" : "text-on-surface"
                      }`}
                    >
                      固定質問{count}つ。深掘り質問は回答内容に応じて最大{count}つ追加されます
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* 出題スタイル */}
          <div className="mb-6">
            <p className="text-sm font-bold mb-3">出題スタイル</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setSelectionMode("auto")}
                className={`relative text-left p-4 rounded-xl border transition-all ${
                  selectionMode === "auto"
                    ? "border-primary bg-white border-2"
                    : "border-gray-300 bg-white hover:border-primary/50"
                }`}
              >
                <RecommendedBadge />
                <div
                  className={`text-sm font-bold mb-1 ${
                    selectionMode === "auto" ? "text-primary" : "text-on-surface"
                  }`}
                >
                  おまかせ
                </div>
                <div className="text-xs text-on-surface leading-relaxed">
                  カテゴリバランスを考慮して自動で出題します
                </div>
              </button>
              <button
                type="button"
                onClick={switchToManual}
                className={`text-left p-4 rounded-xl border transition-all ${
                  selectionMode === "manual"
                    ? "border-primary bg-white border-2"
                    : "border-gray-300 bg-white hover:border-primary/50"
                }`}
              >
                <div
                  className={`text-sm font-bold mb-1 ${
                    selectionMode === "manual" ? "text-primary" : "text-on-surface"
                  }`}
                >
                  自分で選ぶ
                </div>
                <div className="text-xs text-on-surface leading-relaxed">
                  練習したい質問を自分で選びます
                </div>
              </button>
            </div>
          </div>

          {SHOW_JOB_TYPE_FILTER && selectionMode === "auto" && (
            <>
              {/* 職種（複数選択可） */}
              <div className="mb-2">
                <p className="text-sm font-bold mb-3">職種（任意・複数選択可）</p>
                <div className="flex flex-wrap gap-2">
                  {JOB_TYPE_OPTIONS.map((opt) => {
                    const isSelected = jobTypeFilters.includes(opt.value);
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => toggleJobType(opt.value)}
                        aria-pressed={isSelected}
                        className={`px-4 py-2 rounded-full text-sm font-bold border transition-all min-h-[36px] ${
                          isSelected
                            ? "border-primary bg-primary text-white"
                            : "border-primary text-primary bg-white opacity-50 hover:opacity-80"
                        }`}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>

        {/* 右カード: コンテキスト（develop の実機能ピッカー） */}
        <div className="md:col-span-1 mt-6 md:mt-0">
          <InterviewContextPicker
            targetPositions={targetPositions}
            jobHuntingFiles={readyJobHuntingFiles}
            selectedTargetPositionId={targetPositionId}
            selectedJobHuntingFileIds={jobHuntingFileIds}
            onChangeTargetPosition={setTargetPositionId}
            onChangeJobHuntingFileIds={setJobHuntingFileIds}
            onShowToast={showToast}
            loadError={contextLoadError}
          />
        </div>
      </div>

      {/* CTA エリア */}
      <div className="max-w-6xl mx-auto px-4 md:px-10 mt-8 md:mt-10 pb-10 flex flex-col items-center gap-3">
        {error && (
          <div className="bg-error/10 text-error text-sm rounded-xl p-3 text-center w-full max-w-md">
            {error}
          </div>
        )}
        <button
          type="button"
          onClick={handleStart}
          disabled={isLoading}
          className="w-full max-w-md bg-gradient-to-r from-primary to-accent text-white font-bold text-base rounded-lg min-h-[48px] px-12 transition-opacity hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {ctaLabel}
        </button>
        <button
          type="button"
          onClick={() => setShowQuitModal(true)}
          className="text-sm min-h-[44px] hover:text-primary transition-colors"
        >
          キャンセル
        </button>
      </div>

      {showQuitModal && (
        <div
          onClick={() => setShowQuitModal(false)}
          onKeyDown={handleModalKeyDown}
          className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 px-4"
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="quit-modal-title"
            onClick={(e) => e.stopPropagation()}
            className="bg-surface rounded-2xl shadow-xl max-w-sm w-full p-6"
          >
            <h2 id="quit-modal-title" className="text-lg font-bold text-on-surface mb-2 text-center">
              面接モードをやめますか？
            </h2>
            <p className="text-sm text-on-surface-variant text-center mb-6">
              設定中の内容は保存されません。
            </p>
            <div className="flex gap-3">
              <button
                ref={modalNoButtonRef}
                type="button"
                onClick={() => setShowQuitModal(false)}
                className="flex-1 py-2.5 text-sm font-semibold text-on-surface border-[1.5px] border-outline-variant rounded-xl hover:bg-surface-container-highest transition-colors min-h-[44px]"
              >
                いいえ
              </button>
              <button
                ref={modalYesButtonRef}
                type="button"
                onClick={() => router.push("/dashboard")}
                className="flex-1 py-2.5 text-sm font-semibold text-white bg-accent hover:bg-accent-hover rounded-xl transition-colors min-h-[44px]"
              >
                はい
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
